/**
 * Subflow tool processor for the Now Assist Skill Plugin.
 * Handles processing of Subflow tools defined in NowAssistSkillConfig.
 */

import {
    TOOL_TYPES,
    RESOURCE_ATTRIBUTE_VALUE_SCRIPT,
    SYS_ONE_EXTEND_CAPABILITY_DEFINITION_API_TYPES,
} from '../defaults'
import type { Context, ObjectShape, Record as BuildRecord } from '@servicenow/sdk-build-core'
import { applyDefaults, buildDependsOnList, createDependencyEdges } from '../skill-plugin-helpers'
import type { ToolDefinition } from '../tool-types'
import {
    SYS_ONE_EXTEND_CAPABILITY,
    SYS_ONE_EXTEND_CAPABILITY_DEFINITION,
    SYS_ONE_EXTEND_DEFINITION_CONFIG,
    SYS_ONE_EXTEND_DEFINITION_ATTRIBUTE,
    SYS_ONE_EXTEND_RESOURCE_MAPPING,
    SYS_ONE_EXTEND_RESOURCE_ATTRIBUTE_MAPPING,
    SYS_GEN_AI_FEATURE_MAPPING,
    SYS_GEN_AI_STRATEGY_MAPPING,
    getApplicabilityProperties,
    generateInputAttributeValueScript,
    normalizeInputFieldName,
    validateToolDataType,
} from '../tool-utils'

/**
 * Process a Subflow tool and create the necessary records
 *
 * Subflow tools create:
 * 1. sys_one_extend_resource_mapping - The main tool record
 * 2. sys_one_extend_resource_attribute_mapping - Input attribute mappings
 * 3. sys_one_extend_resource_attribute_mapping - Output attribute mapping (fixed: Output, type: object)
 * 4. sys_one_extend_resource_edge - Dependency edges (if depends is specified)
 */
export async function processSubflowTool(
    tool: ToolDefinition,
    capabilityRecord: BuildRecord,
    toolRecordsMap: Map<string, BuildRecord>,
    arg: ObjectShape,
    factory: Context['factory'],
    diagnostics: Context['diagnostics'],
    decisionToolNames?: Set<string>
): Promise<BuildRecord[]> {
    const records: BuildRecord[] = []

    // Validate tool type
    if (tool.type !== TOOL_TYPES.SUBFLOW) {
        diagnostics.error(arg, `Expected subflow tool but got ${tool.type}`)
        return records
    }

    // Validate required fields
    if (!tool.name) {
        diagnostics.error(arg, 'Subflow tool requires a name')
        return records
    }

    if (!tool.subflowId) {
        diagnostics.error(arg, 'Subflow tool requires a subflowId')
        return records
    }

    // Build depends_on_list from dependency names
    const dependsOnList = buildDependsOnList(tool, toolRecordsMap, arg, diagnostics, decisionToolNames)

    // Build capability name using subflowId and tool name (matches platform pattern)
    const capabilityName = `${tool.subflowId}_${tool.name}`
    // 0a. Create sys_one_extend_capability record for the Subflow tool
    // This is required - resource_capability must point to this capability, not the subflowId directly
    // When tool.$capabilityId is present, use it directly as the capability explicitId.
    const toolCapabilityExplicitId = tool.$capabilityId
        ? tool.$capabilityId
        : `${capabilityRecord.getId().toString().getValue()}__tool_${tool.name}`
    const toolCapabilityRecord = await factory.createRecord({
        source: tool._source!,
        table: SYS_ONE_EXTEND_CAPABILITY,
        explicitId: toolCapabilityExplicitId,
        properties: arg.transform(({ $ }) => ({
            ...applyDefaults(SYS_ONE_EXTEND_CAPABILITY, $),
            name: $.val(capabilityName),
            type: $.val('datasource'),
        })),
    })
    records.push(toolCapabilityRecord)

    // 0b. Create sys_one_extend_capability_definition record for the Subflow tool
    const toolCapDefRecord = await factory.createRecord({
        source: tool._source!,
        table: SYS_ONE_EXTEND_CAPABILITY_DEFINITION,
        properties: arg.transform(({ $ }) => ({
            ...applyDefaults(SYS_ONE_EXTEND_CAPABILITY_DEFINITION, $),
            name: $.val(capabilityName),
            capability: $.val(toolCapabilityRecord.getId()),
            api: $.val(tool.subflowId),
            api_type: $.val(SYS_ONE_EXTEND_CAPABILITY_DEFINITION_API_TYPES.SYS_HUB_FLOW),
        })),
    })
    records.push(toolCapDefRecord)

    // 0c. Create sys_one_extend_definition_config record
    const toolDefConfigRecord = await factory.createRecord({
        source: tool._source!,
        table: SYS_ONE_EXTEND_DEFINITION_CONFIG,
        properties: arg.transform(({ $ }) => ({
            ...applyDefaults(SYS_ONE_EXTEND_DEFINITION_CONFIG, $),
            capability: $.val(toolCapabilityRecord.getId()),
            definition: $.val(toolCapDefRecord.getId()),
            default: $.val('1'),
        })),
    })
    records.push(toolDefConfigRecord)

    // 0d. Create sys_one_extend_definition_attribute records for inputs
    // Store records in a map keyed by input name so we can reference them in attribute mappings
    const inputDefAttrRecordsMap = new Map<string, BuildRecord>()
    if (tool.inputs) {
        for (const input of tool.inputs) {
            // Validate input type for Subflow tool
            const inputTypeError = validateToolDataType(input.type, input.name, tool.name, true)
            if (inputTypeError) {
                diagnostics.error(arg, inputTypeError)
                return records
            }

            const internalName = normalizeInputFieldName(input.name)
            const inputDataType = input.type || 'string'
            const inputDefAttrRecord = await factory.createRecord({
                source: tool._source!,
                table: SYS_ONE_EXTEND_DEFINITION_ATTRIBUTE,
                explicitId: input.$id,
                properties: arg.transform(({ $ }) => ({
                    ...applyDefaults(SYS_ONE_EXTEND_DEFINITION_ATTRIBUTE, $),
                    capability: $.val(toolCapabilityRecord.getId()),
                    name: $.val(internalName),
                    label: $.val(input.name),
                    type: $.val('input'),
                    data_type: $.val(inputDataType),
                })),
            })
            records.push(inputDefAttrRecord)
            inputDefAttrRecordsMap.set(input.name, inputDefAttrRecord)
        }
    }

    // 0e. Create sys_one_extend_definition_attribute records for outputs
    // Store records in a map keyed by output name so we can reference them in attribute mappings
    const outputDefAttrRecordsMap = new Map<string, BuildRecord>()
    if (tool.outputs && tool.outputs.length > 0) {
        for (const output of tool.outputs) {
            // Validate output type for Subflow tool
            const outputTypeError = validateToolDataType(output.type, output.name, tool.name, false)
            if (outputTypeError) {
                diagnostics.error(arg, outputTypeError)
                return records
            }

            const internalName = normalizeInputFieldName(output.name)
            const outputDataType = output.type || 'object'
            const outputDefAttrRecord = await factory.createRecord({
                source: tool._source!,
                table: SYS_ONE_EXTEND_DEFINITION_ATTRIBUTE,
                explicitId: output.$id,
                properties: arg.transform(({ $ }) => ({
                    ...applyDefaults(SYS_ONE_EXTEND_DEFINITION_ATTRIBUTE, $),
                    capability: $.val(toolCapabilityRecord.getId()),
                    name: $.val(internalName),
                    label: $.val(output.name),
                    type: $.val('output'),
                    data_type: $.val(outputDataType),
                })),
            })
            records.push(outputDefAttrRecord)
            outputDefAttrRecordsMap.set(output.name, outputDefAttrRecord)
        }
    } else {
        // Default 'Output' definition attribute for backward compatibility
        const defaultOutputDefAttrRecord = await factory.createRecord({
            source: tool._source!,
            table: SYS_ONE_EXTEND_DEFINITION_ATTRIBUTE,
            explicitId: `${toolCapabilityExplicitId}__defattr_output`,
            properties: arg.transform(({ $ }) => ({
                ...applyDefaults(SYS_ONE_EXTEND_DEFINITION_ATTRIBUTE, $),
                capability: $.val(toolCapabilityRecord.getId()),
                name: $.val('output'),
                label: $.val('Output'),
                type: $.val('output'),
            })),
        })
        records.push(defaultOutputDefAttrRecord)
        outputDefAttrRecordsMap.set('Output', defaultOutputDefAttrRecord)
    }

    // 0f. Create sys_gen_ai_feature_mapping record
    const featureMappingRecord = await factory.createRecord({
        source: tool._source!,
        table: SYS_GEN_AI_FEATURE_MAPPING,
        properties: arg.transform(({ $ }) => ({
            ...applyDefaults(SYS_GEN_AI_FEATURE_MAPPING, $),
            document: $.val(toolCapabilityRecord.getId()),
            document_table: $.val('sys_one_extend_capability'),
            feature_name: $.val(capabilityName),
        })),
    })
    records.push(featureMappingRecord)

    // 0g. Create sys_gen_ai_strategy_mapping record
    const strategyMappingRecord = await factory.createRecord({
        source: tool._source!,
        table: SYS_GEN_AI_STRATEGY_MAPPING,
        properties: arg.transform(({ $ }) => ({
            ...applyDefaults(SYS_GEN_AI_STRATEGY_MAPPING, $),
            feature: $.val(featureMappingRecord.getId()),
            strategy: $.val('CAPABILITY_EXECUTION'),
        })),
    })
    records.push(strategyMappingRecord)

    // Build metadata with toolType for reverse detection
    const metadata: Record<string, unknown> = {
        toolType: TOOL_TYPES.SUBFLOW,
        subflowId: tool.subflowId,
    }

    // 1. Create sys_one_extend_resource_mapping record
    // resource_capability points to the tool's capability record, NOT the subflowId
    const resourceMappingRecord = await factory.createRecord({
        source: tool._source!,
        table: SYS_ONE_EXTEND_RESOURCE_MAPPING,
        ...(tool.$id ? { explicitId: tool.$id } : {}),
        properties: arg.transform(({ $ }) => ({
            ...applyDefaults(SYS_ONE_EXTEND_RESOURCE_MAPPING, $),
            ...getApplicabilityProperties(tool.condition, $),
            depends_on_list: $.val(dependsOnList),
            metadata: $.val(JSON.stringify(metadata)),
            parent_capability: $.val(capabilityRecord.getId()),
            resource_capability: $.val(toolCapabilityRecord.getId()),
            resource_name: $.val(tool.name),
        })),
    })
    records.push(resourceMappingRecord)

    // Store in map for dependency resolution
    toolRecordsMap.set(tool.name, resourceMappingRecord)

    // 2. Create sys_one_extend_resource_attribute_mapping for inputs
    if (tool.inputs) {
        for (const input of tool.inputs) {
            const isScripted = input.valueType === 'script'
            const inputScript = isScripted
                ? input.scriptValue!
                : generateInputAttributeValueScript(input.name, input.value)
            const inputDefAttrRecord = inputDefAttrRecordsMap.get(input.name)
            const inputAttributeRecord = await factory.createRecord({
                source: tool._source!,
                table: SYS_ONE_EXTEND_RESOURCE_ATTRIBUTE_MAPPING,
                properties: arg.transform(({ $ }) => ({
                    ...applyDefaults(SYS_ONE_EXTEND_RESOURCE_ATTRIBUTE_MAPPING, $),
                    attribute_value_script: $.val(inputScript).toCdata(),
                    attribute_value_template: $.val(input.value),
                    attribute_value_type: $.val(isScripted ? 'script' : 'template'),
                    resource_attribute_name: inputDefAttrRecord ? $.val(inputDefAttrRecord.getId()) : $.val(input.name),
                    resource_mapping: $.val(resourceMappingRecord.getId()),
                    type: $.val('input'),
                })),
            })
            records.push(inputAttributeRecord)
        }
    }

    // 3. Create sys_one_extend_resource_attribute_mapping for outputs
    if (tool.outputs && tool.outputs.length > 0) {
        // Dynamic outputs specified by user
        for (const output of tool.outputs) {
            const outputTruncate = output.truncate ?? false
            const outputDefAttrRecord = outputDefAttrRecordsMap.get(output.name)
            const outputAttributeRecord = await factory.createRecord({
                source: tool._source!,
                table: SYS_ONE_EXTEND_RESOURCE_ATTRIBUTE_MAPPING,
                properties: arg.transform(({ $ }) => ({
                    ...applyDefaults(SYS_ONE_EXTEND_RESOURCE_ATTRIBUTE_MAPPING, $),
                    attribute_value_script: $.val(RESOURCE_ATTRIBUTE_VALUE_SCRIPT).toCdata(),
                    resource_attribute_name: outputDefAttrRecord
                        ? $.val(outputDefAttrRecord.getId())
                        : $.val(output.name),
                    resource_mapping: $.val(resourceMappingRecord.getId()),
                    truncate: $.val(outputTruncate),
                    type: $.val('output'),
                })),
            })
            records.push(outputAttributeRecord)
        }
    } else {
        // Fallback: single 'Output' for backward compatibility
        const truncate = tool.truncate ?? false
        const defaultOutputDefAttrRecord = outputDefAttrRecordsMap.get('Output')
        const outputAttributeRecord = await factory.createRecord({
            source: tool._source!,
            table: SYS_ONE_EXTEND_RESOURCE_ATTRIBUTE_MAPPING,
            properties: arg.transform(({ $ }) => ({
                ...applyDefaults(SYS_ONE_EXTEND_RESOURCE_ATTRIBUTE_MAPPING, $),
                attribute_value_script: $.val(RESOURCE_ATTRIBUTE_VALUE_SCRIPT).toCdata(),
                resource_attribute_name: defaultOutputDefAttrRecord
                    ? $.val(defaultOutputDefAttrRecord.getId())
                    : $.val('Output'),
                resource_mapping: $.val(resourceMappingRecord.getId()),
                truncate: $.val(truncate),
                type: $.val('output'),
            })),
        })
        records.push(outputAttributeRecord)
    }

    // 4. Create sys_one_extend_resource_edge records for dependencies
    const edgeRecords = await createDependencyEdges(
        tool,
        toolRecordsMap,
        resourceMappingRecord,
        arg,
        factory,
        true,
        tool._source!
    )
    records.push(...edgeRecords)

    return records
}
